import { useContext, useRef } from 'react';

import folderMoveIcon from '@atom/components/common/svgIcons/folderMove.svg';
import { Icon, IconButton, Menu } from '@atom/mui';
import {
  CreateNewFolderIcon,
  DeleteIcon,
  EditIcon,
  FileDownloadIcon,
  FileUploadIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';

import { ROOT_FOLDER_ID } from './constants';
import FilesTabContext from './FilesTabContext';
import { canCreateSubfolder } from './filesTabUtilities';
import SearchBox from './SearchBox';
import { ModalType } from './types';

import classes from './filesTab.module.css';

const { MenuItem } = Menu;

const styles = {
  hiddenInput: {
    display: 'none',
  },
};

type FilesTabHeaderProps = {
  searchValue: string;
  onSearchChange: (next: string) => void;
  openModal: (type: ModalType) => void;
};

const FilesTabHeader = ({
  searchValue,
  onSearchChange,
  openModal,
}: FilesTabHeaderProps) => {
  const {
    canCreate,
    canUpdate,
    canDelete,
    selectedFolderId,
    selectedFolderDepth,
    selectedMediaType,
    selectedItems,
    media,
    uploadAndRefetch,
    downloadSelected,
  } = useContext(FilesTabContext);

  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files.length === 0) {
      return;
    }

    await uploadAndRefetch({ files });

    if (fileUploadRef.current) {
      fileUploadRef.current.value = '';
    }
  };

  const handleDownloadSelected = () => {
    const selectedMedia = media.filter(item => selectedItems.has(item.id));
    downloadSelected(selectedMedia);
  };

  const isCreateDisabled = !canCreateSubfolder(selectedFolderDepth);

  const showUploadButton = !selectedMediaType && canCreate;
  const showCreateButton = !selectedMediaType && canCreate;
  const showFolderActions =
    selectedFolderId !== ROOT_FOLDER_ID &&
    selectedFolderId !== '' &&
    !selectedMediaType &&
    (canDelete || canUpdate);

  const hasSelection = selectedItems.size > 0;

  const selectionActions = (
    <>
      {canUpdate && (
        <IconButton
          tooltip="Move to Folder"
          onClick={() => openModal(ModalType.MOVE_MEDIA)}
          data-cy="filesTabMoveToFolderButton"
        >
          <img src={folderMoveIcon} />
        </IconButton>
      )}
      <IconButton
        tooltip="Download"
        onClick={handleDownloadSelected}
        data-cy="filesTabDownloadButton"
      >
        <Icon color={colors.neutral.gray} icon={FileDownloadIcon} />
      </IconButton>
      {canDelete && (
        <IconButton
          tooltip="Delete"
          onClick={() => openModal(ModalType.BULK_DELETE)}
          data-cy="filesTabBulkDeleteButton"
        >
          <Icon color={colors.neutral.gray} icon={DeleteIcon} />
        </IconButton>
      )}
    </>
  );

  const defaultActions = (
    <>
      <SearchBox value={searchValue} onChange={onSearchChange} />
      {showCreateButton && (
        <IconButton
          tooltip="Create Folder"
          onClick={() => openModal(ModalType.CREATE_FOLDER)}
          disabled={isCreateDisabled}
          data-cy="filesTabCreateFolderButton"
        >
          <Icon color={colors.neutral.gray} icon={CreateNewFolderIcon} />
        </IconButton>
      )}
      {showUploadButton && (
        <>
          <IconButton
            tooltip="Upload"
            onClick={() => fileUploadRef.current?.click()}
            data-cy="filesTabUploadButton"
          >
            <Icon color={colors.neutral.gray} icon={FileUploadIcon} />
          </IconButton>
          <input
            type="file"
            style={styles.hiddenInput}
            multiple
            ref={fileUploadRef}
            onChange={handleUpload}
          />
        </>
      )}
      {showFolderActions && (
        <Menu dataCyLabel="filesTabFolderActionsMenu">
          {canUpdate && (
            <MenuItem
              startAdornment={<Icon icon={EditIcon} />}
              onClick={() => openModal(ModalType.EDIT_FOLDER)}
              data-cy="filesTabFolderEditMenuItem"
            >
              Edit
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem
              startAdornment={<Icon icon={DeleteIcon} />}
              onClick={() => openModal(ModalType.DELETE_FOLDER)}
              data-cy="filesTabFolderDeleteMenuItem"
            >
              Delete
            </MenuItem>
          )}
        </Menu>
      )}
    </>
  );

  return (
    <div className={classes.buttonContainer}>
      {hasSelection ? selectionActions : defaultActions}
    </div>
  );
};

export default FilesTabHeader;
